import { Query } from '../../../hooks/queries'
import { useContrastQuery } from '../../../hooks/use-contrast-query'
import { Field } from '../../add-agent/custom-fields-form'
import { getCustomMetadataFields } from './requests/get-custom-metadata-fields'
import { useOrganization } from '../../organization-licenses/api/use-organization'

export interface UseCustomMetadataFieldsResponse {
  isLoading: boolean
  customMetadataFields: Field[]
}

type UseCustomMetadataFields = (props: {
  organizationUuid: string
}) => UseCustomMetadataFieldsResponse

export const useCustomMetadataFields: UseCustomMetadataFields = ({
  organizationUuid
}) => {
  const organizationData = useOrganization({
    organizationUuid,
    expandValue: 'freemium'
  })

  // Enable custom metadata fields request only
  // after organization has been retrieved
  // and only if the organization is not freemium
  const getCustomMetadataFieldsIsEnabled =
    !organizationData.isLoading &&
    !organizationData.isError &&
    !organizationData.data?.freemium

  const customMetadataData = useContrastQuery(
    [Query.CUSTOM_METADATA_FIELDS, organizationUuid],
    getCustomMetadataFields,
    { organizationUuid },
    {
      enabled: getCustomMetadataFieldsIsEnabled,
      cacheTime: 0
    }
  )

  return {
    isLoading: organizationData.isLoading || customMetadataData.isLoading,
    customMetadataFields: !!customMetadataData.data
      ? (normalizeCustomMetadataFields(customMetadataData.data) as Field[])
      : []
  }
}

interface BaseCustomMetadataField {
  type: string
  displayLabel: string
  agentLabel: string
  required?: boolean
  unique?: boolean
}

interface BaseCustomMetadataSubfield {
  type: string
  displayLabel: string
  agentLabel: string
  required?: boolean
  unique?: boolean
}

interface CustomMetadataSubfield extends BaseCustomMetadataSubfield {
  id: string
}

export interface CustomMetadataField extends BaseCustomMetadataField {
  id: string
  subfields?: CustomMetadataSubfield[]
}

export function normalizeCustomMetadataFields(fields): CustomMetadataField[] {
  return fields.map(
    ({
      fieldId,
      fieldType,
      displayLabel,
      agentLabel,
      required,
      unique,
      subfields
    }) => {
      return {
        id: fieldId,
        type: fieldType,
        displayLabel,
        agentLabel,
        required,
        unique,
        subfields:
          !!subfields && !!subfields.length ? normalizeSubfields(subfields) : []
      }
    }
  )
}

function normalizeSubfields(subfields): CustomMetadataSubfield[] {
  return subfields.map(
    ({ fieldId, fieldType, displayLabel, agentLabel, required, unique }) => {
      return {
        id: fieldId,
        type: fieldType,
        displayLabel,
        agentLabel,
        required,
        unique
      }
    }
  )
}
